import {
  denormalisedResponseEntities,
  getCurrentUserReactions,
  isWindowDefined,
} from '../util/data';
import { storableError } from '../util/errors';
import { LISTING_STATE_PUBLISHED } from '../util/types';
import * as log from '../util/log';
import { authInfo } from './Auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { intercomAPI } from '../util/api';
import { usersAPI, listingsAPI } from '../api';
import { imageVariantsForSets, listingCardIncludeParams } from '../util/imageVariants';
import { addMarketplaceEntities } from './marketplaceData.duck';
import { notification } from '../util/notification';
import { fetchUnreadCounts } from './sendbird.duck';
import { LOCALE_COOKIE, pathForLocale } from '../util/locale';
import { goToUrl } from '../util/navigation';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';
export const CURRENT_USER_EXTRAS_FETCHED = 'app/user/CURRENT_USER_EXTRAS_FETCHED';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const CURRENT_USER_ONLINE_REQUEST = 'app/user/CURRENT_USER_ONLINE_REQUEST';
export const CURRENT_USER_ONLINE_SUCCESS = 'app/user/CURRENT_USER_ONLINE_SUCCESS';
export const CURRENT_USER_ONLINE_ERROR = 'app/user/CURRENT_USER_ONLINE_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

// Action types for following the user which is currently being
// used in containers/ListingPage.js and containers/LiveRoomPage.js.
export const FOLLOW_USER_REQUEST = 'app/user/FOLLOW_USER_REQUEST';
export const FOLLOW_USER_SUCCESS = 'app/user/FOLLOW_USER_SUCCESS';
export const FOLLOW_USER_ERROR = 'app/user/FOLLOW_USER_ERROR';

export const UNFOLLOW_USER_REQUEST = 'app/user/UNFOLLOW_USER_REQUEST';
export const UNFOLLOW_USER_SUCCESS = 'app/user/UNFOLLOW_USER_SUCCESS';
export const UNFOLLOW_USER_ERROR = 'app/user/UNFOLLOW_USER_ERROR';

export const ADD_LISTING_TO_WATCHLIST_REQUEST = 'app/user/ADD_LISTING_TO_WATCHLIST_REQUEST';
export const ADD_LISTING_TO_WATCHLIST_SUCCESS = 'app/user/ADD_LISTING_TO_WATCHLIST_SUCCESS';
export const ADD_LISTING_TO_WATCHLIST_ERROR = 'app/user/ADD_LISTING_TO_WATCHLIST_ERROR';

export const REMOVE_LISTING_FROM_WATCHLIST_REQUEST =
  'app/user/REMOVE_LISTING_FROM_WATCHLIST_REQUEST';
export const REMOVE_LISTING_FROM_WATCHLIST_SUCCESS =
  'app/user/REMOVE_LISTING_FROM_WATCHLIST_SUCCESS';
export const REMOVE_LISTING_FROM_WATCHLIST_ERROR = 'app/user/REMOVE_LISTING_FROM_WATCHLIST_ERROR';

export const SAVE_RECENTLY_VIEWED_LISTING_REQUEST = 'app/user/SAVE_RECENTLY_VIEWED_LISTING_REQUEST';
export const SAVE_RECENTLY_VIEWED_LISTING_SUCCESS = 'app/user/SAVE_RECENTLY_VIEWED_LISTING_SUCCESS';
export const SAVE_RECENTLY_VIEWED_LISTING_ERROR = 'app/user/SAVE_RECENTLY_VIEWED_LISTING_ERROR';

export const QUERY_RECENTLY_VIEWED_LISTINGS_REQUEST =
  'app/user/QUERY_RECENTLY_VIEWED_LISTINGS_REQUEST';
export const QUERY_RECENTLY_VIEWED_LISTINGS_SUCCESS =
  'app/user/QUERY_RECENTLY_VIEWED_LISTINGS_SUCCESS';
export const QUERY_RECENTLY_VIEWED_LISTINGS_ERROR =
  'app//user/QUERY_RECENTLY_VIEWED_LISTINGS_ERROR';

export const SAVE_BILLING_DETAILS_REQUEST = 'app/user/SAVE_BILLING_DETAILS_REQUEST';
export const SAVE_BILLING_DETAILS_SUCCESS = 'app/user/SAVE_BILLING_DETAILS_SUCCESS';
export const SAVE_BILLING_DETAILS_ERROR = 'app/user/SAVE_BILLING_DETAILS_ERROR';

export const REMOVE_BILLING_DETAILS_REQUEST = 'app/user/REMOVE_BILLING_DETAILS_REQUEST';
export const REMOVE_BILLING_DETAILS_SUCCESS = 'app/user/REMOVE_BILLING_DETAILS_SUCCESS';
export const REMOVE_BILLING_DETAILS_ERROR = 'app/user/REMOVE_BILLING_DETAILS_ERROR';

export const GENERATE_INTERCOM_USER_HASH_REQUEST = 'app/user/GENERATE_INTERCOM_USER_HASH_REQUEST';
export const GENERATE_INTERCOM_USER_HASH_SUCCESS = 'app/user/GENERATE_INTERCOM_USER_HASH_SUCCESS';
export const GENERATE_INTERCOM_USER_HASH_ERROR = 'app/user/GENERATE_INTERCOM_USER_HASH_ERROR';

export const FETCH_CURRENT_USER_REACTIONS_SUCCESS = 'app/user/FETCH_CURRENT_USER_REACTIONS_SUCCESS';

export const UPGRADE_USER_ROLE_REQUEST = 'app/user/UPGRADE_USER_ROLE_REQUEST';
export const UPGRADE_USER_ROLE_SUCCESS = 'app/user/UPGRADE_USER_ROLE_SUCCESS';
export const UPGRADE_USER_ROLE_ERROR = 'app/user/UPGRADE_USER_ROLE_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  // When the listings, notification and orders lookups that follow
  // fetchCurrentUser last ran, and for whom (see EXTRAS_TTL_MS).
  currentUserExtrasFetchedAt: null,
  currentUserExtrasUserId: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  currentUserListing: null,
  currentUserListingFetched: false,
  currentUserOnlineError: null,
  currentUserReactions: [],
  followUserInProgress: false,
  followUserError: null,
  unfollowUserInProgress: false,
  unfollowUserError: null,
  addListingToWatchlistInProgress: false,
  addListingToWatchlistError: null,
  removeListingFromWatchlistInProgress: false,
  removeListingFromWatchlistError: null,
  currentWatchlistListingId: null,
  currentWatchlistErrorListingIds: [],
  saveRecentlyViewedListingInProgress: false,
  saveRecentlyViewedListingError: null,
  recentlyViewedListingIds: [],
  queryRecentlyViewedListingsInProgress: false,
  queryRecentlyViewedListingsError: null,
  saveBillingDetailsInProgress: false,
  saveBillingDetailsError: null,
  removeBillingDetailsInProgress: false,
  removeBillingDetailsError: null,
  generateIntercomUserHashInProgress: false,
  generateIntercomUserHashError: null,
  intercomUserHash: null,
  upgradeUserRoleInProgress: false,
  upgradeUserRoleError: null,
};

const resultIds = data => data.data.map(d => d.id);

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserListing: null,
        currentUserListingFetched: false,
        currentUserExtrasFetchedAt: null,
        currentUserExtrasUserId: null,
      };

    case CURRENT_USER_EXTRAS_FETCHED:
      return {
        ...state,
        currentUserExtrasFetchedAt: payload.at,
        currentUserExtrasUserId: payload.userId,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return {
        ...state,
        currentUserHasListings: payload.hasListings,
        currentUserListing: payload.listing,
        currentUserListingFetched: true,
      };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case CURRENT_USER_ONLINE_REQUEST:
      return {
        ...state,
      };
    case CURRENT_USER_ONLINE_SUCCESS:
      return {
        ...state,
      };
    case CURRENT_USER_ONLINE_ERROR:
      return {
        ...state,
        currentUserOnlineError: payload,
      };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case FOLLOW_USER_REQUEST:
      return {
        ...state,
        followUserInProgress: true,
        followUserError: null,
      };
    case FOLLOW_USER_SUCCESS:
      return {
        ...state,
        followUserInProgress: false,
        followUserError: null,
      };
    case FOLLOW_USER_ERROR:
      return {
        ...state,
        followUserInProgress: false,
        followUserError: payload,
      };

    case UNFOLLOW_USER_REQUEST:
      return {
        ...state,
        unfollowUserInProgress: true,
        unfollowUserError: null,
      };
    case UNFOLLOW_USER_SUCCESS:
      return {
        ...state,
        unfollowUserInProgress: false,
        unfollowUserError: null,
      };
    case UNFOLLOW_USER_ERROR:
      return {
        ...state,
        unfollowUserInProgress: false,
        unfollowUserError: payload,
      };

    case ADD_LISTING_TO_WATCHLIST_REQUEST:
      return {
        ...state,
        currentWatchlistListingId: payload.listingId,
        addListingToWatchlistInProgress: true,
        addListingToWatchlistError: null,
      };

    case ADD_LISTING_TO_WATCHLIST_SUCCESS:
      return {
        ...state,
        currentWatchlistListingId: null,
        addListingToWatchlistInProgress: false,
        addListingToWatchlistError: null,
      };
    case ADD_LISTING_TO_WATCHLIST_ERROR:
      return {
        ...state,
        currentWatchlistListingId: null,
        currentWatchlistErrorListingIds: [
          ...state.currentWatchlistErrorListingIds,
          payload.listingId,
        ],
        addListingToWatchlistInProgress: false,
        addListingToWatchlistError: payload.error,
      };

    case REMOVE_LISTING_FROM_WATCHLIST_REQUEST:
      return {
        ...state,
        currentWatchlistListingId: payload.listingId,
        removeListingFromWatchlistInProgress: true,
        removeListingFromWatchlistError: null,
      };

    case REMOVE_LISTING_FROM_WATCHLIST_SUCCESS:
      return {
        ...state,
        currentWatchlistListingId: null,
        removeListingFromWatchlistInProgress: false,
        removeListingFromWatchlistError: null,
      };
    case REMOVE_LISTING_FROM_WATCHLIST_ERROR:
      return {
        ...state,
        currentWatchlistListingId: null,
        currentWatchlistErrorListingIds: [
          ...state.currentWatchlistErrorListingIds,
          payload.listingId,
        ],
        removeListingFromWatchlistInProgress: false,
        removeListingFromWatchlistError: payload.error,
      };

    case SAVE_RECENTLY_VIEWED_LISTING_REQUEST:
      return {
        ...state,
        saveRecentlyViewedListingInProgress: true,
        saveRecentlyViewedListingError: null,
      };
    case SAVE_RECENTLY_VIEWED_LISTING_SUCCESS:
      return {
        ...state,
        saveRecentlyViewedListingInProgress: false,
        saveRecentlyViewedListingError: null,
      };
    case SAVE_RECENTLY_VIEWED_LISTING_ERROR:
      return {
        ...state,
        saveRecentlyViewedListingInProgress: false,
        saveRecentlyViewedListingError: null,
      };

    case QUERY_RECENTLY_VIEWED_LISTINGS_REQUEST:
      return {
        ...state,
        queryRecentlyViewedListingsInProgress: true,
        queryRecentlyViewedListingsError: null,
        recentlyViewedListingIds: [],
      };
    case QUERY_RECENTLY_VIEWED_LISTINGS_SUCCESS:
      return {
        ...state,
        queryRecentlyViewedListingsInProgress: false,
        queryRecentlyViewedListingsError: null,
        recentlyViewedListingIds: resultIds(payload.data),
      };
    case QUERY_RECENTLY_VIEWED_LISTINGS_ERROR:
      return {
        ...state,
        queryRecentlyViewedListingsInProgress: false,
        queryRecentlyViewedListingsError: payload,
      };

    case SAVE_BILLING_DETAILS_REQUEST:
      return {
        ...state,
        saveBillingDetailsInProgress: true,
        saveBillingDetailsError: null,
      };
    case SAVE_BILLING_DETAILS_SUCCESS:
      return {
        ...state,
        saveBillingDetailsInProgress: false,
        saveBillingDetailsError: null,
      };
    case SAVE_BILLING_DETAILS_ERROR:
      return {
        ...state,
        saveBillingDetailsInProgress: false,
        saveBillingDetailsError: payload,
      };

    case REMOVE_BILLING_DETAILS_REQUEST:
      return {
        ...state,
        removeBillingDetailsInProgress: true,
        removeBillingDetailsError: null,
      };
    case REMOVE_BILLING_DETAILS_SUCCESS:
      return {
        ...state,
        removeBillingDetailsInProgress: false,
        removeBillingDetailsError: null,
      };
    case REMOVE_BILLING_DETAILS_ERROR:
      return {
        ...state,
        removeBillingDetailsInProgress: false,
        removeBillingDetailsError: payload,
      };

    case GENERATE_INTERCOM_USER_HASH_REQUEST:
      return {
        ...state,
        generateIntercomUserHashInProgress: true,
        generateIntercomUserHashError: null,
        intercomUserHash: null,
      };
    case GENERATE_INTERCOM_USER_HASH_SUCCESS:
      return {
        ...state,
        generateIntercomUserHashInProgress: false,
        generateIntercomUserHashError: null,
        intercomUserHash: payload.hash,
      };
    case GENERATE_INTERCOM_USER_HASH_ERROR:
      return {
        ...state,
        generateIntercomUserHashInProgress: false,
        generateIntercomUserHashError: payload,
        intercomUserHash: null,
      };

    case FETCH_CURRENT_USER_REACTIONS_SUCCESS:
      return {
        ...state,
        currentUserReactions: payload.currentUserReactions,
      };

    case UPGRADE_USER_ROLE_REQUEST:
      return {
        ...state,
        upgradeUserRoleInProgress: true,
        upgradeUserRoleError: null,
      };
    case UPGRADE_USER_ROLE_SUCCESS:
      return {
        ...state,
        upgradeUserRoleInProgress: false,
        upgradeUserRoleError: null,
      };
    case UPGRADE_USER_ROLE_ERROR:
      return {
        ...state,
        upgradeUserRoleInProgress: false,
        upgradeUserRoleError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError || user.currentUserHasListingsError || user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });
const currentUserExtrasFetched = (userId, at) => ({
  type: CURRENT_USER_EXTRAS_FETCHED,
  payload: { userId, at },
});

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = (hasListings, listing) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings, listing },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const currentUserOnlineRequest = () => ({
  type: CURRENT_USER_ONLINE_REQUEST,
});
export const currentUserOnlineSuccess = () => ({
  type: CURRENT_USER_ONLINE_SUCCESS,
});
export const currentUserOnlineError = () => ({
  type: CURRENT_USER_ONLINE_ERROR,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const followUserRequest = () => ({
  type: FOLLOW_USER_REQUEST,
});
export const followUserSuccess = () => ({
  type: FOLLOW_USER_SUCCESS,
});
export const followUserError = e => ({
  type: FOLLOW_USER_ERROR,
  error: true,
  payload: e,
});

export const unfollowUserRequest = () => ({
  type: UNFOLLOW_USER_REQUEST,
});
export const unfollowUserSuccess = () => ({
  type: UNFOLLOW_USER_SUCCESS,
});
export const unfollowUserError = e => ({
  type: UNFOLLOW_USER_ERROR,
  error: true,
  payload: e,
});

export const addListingToWatchlistRequest = listingId => ({
  type: ADD_LISTING_TO_WATCHLIST_REQUEST,
  payload: { listingId },
});
export const addListingToWatchlistSuccess = () => ({
  type: ADD_LISTING_TO_WATCHLIST_SUCCESS,
});
export const addListingToWatchlistError = (e, listingId) => ({
  type: ADD_LISTING_TO_WATCHLIST_ERROR,
  payload: { error: e, listingId },
});

export const removeListingFromWatchlistRequest = listingId => ({
  type: REMOVE_LISTING_FROM_WATCHLIST_REQUEST,
  payload: { listingId },
});
export const removeListingFromWatchlistSuccess = () => ({
  type: REMOVE_LISTING_FROM_WATCHLIST_SUCCESS,
});
export const removeListingFromWatchlistError = (e, listingId) => ({
  type: REMOVE_LISTING_FROM_WATCHLIST_ERROR,
  payload: { error: e, listingId },
});

export const saveRecentlyViewedListingRequest = () => ({
  type: SAVE_RECENTLY_VIEWED_LISTING_REQUEST,
});
export const saveRecentlyViewedListingSuccess = () => ({
  type: SAVE_RECENTLY_VIEWED_LISTING_SUCCESS,
});
export const saveRecentlyViewedListingError = e => ({
  type: SAVE_RECENTLY_VIEWED_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryRecentlyViewedListingsRequest = () => ({
  type: QUERY_RECENTLY_VIEWED_LISTINGS_REQUEST,
});
export const queryRecentlyViewedListingsSuccess = response => ({
  type: QUERY_RECENTLY_VIEWED_LISTINGS_SUCCESS,
  payload: { data: response.data },
});
export const queryRecentlyViewedListingsError = e => ({
  type: QUERY_RECENTLY_VIEWED_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const saveBillingDetailsRequest = () => ({
  type: SAVE_BILLING_DETAILS_REQUEST,
});
export const saveBillingDetailsSuccess = () => ({
  type: SAVE_BILLING_DETAILS_SUCCESS,
});
export const saveBillingDetailsError = e => ({
  type: SAVE_BILLING_DETAILS_ERROR,
  error: true,
  payload: e,
});

export const removeBillingDetailsRequest = () => ({
  type: REMOVE_BILLING_DETAILS_REQUEST,
});
export const removeBillingDetailsSuccess = () => ({
  type: REMOVE_BILLING_DETAILS_SUCCESS,
});
export const removeBillingDetailsError = e => ({
  type: REMOVE_BILLING_DETAILS_ERROR,
  error: true,
  payload: e,
});

export const generateIntercomUserHashRequest = () => ({
  type: GENERATE_INTERCOM_USER_HASH_REQUEST,
});
export const generateIntercomUserHashSuccess = response => ({
  type: GENERATE_INTERCOM_USER_HASH_SUCCESS,
  payload: { hash: response.hash },
});
export const generateIntercomUserHashError = e => ({
  type: GENERATE_INTERCOM_USER_HASH_ERROR,
  error: true,
  payload: e,
});

export const upgradeUserRoleRequest = () => ({
  type: UPGRADE_USER_ROLE_REQUEST,
});
export const upgradeUserRoleSuccess = () => ({
  type: UPGRADE_USER_ROLE_SUCCESS,
});
export const upgradeUserRoleError = e => ({
  type: UPGRADE_USER_ROLE_ERROR,
  error: true,
  payload: e,
});

export const fetchCurrentUserReactionsSuccess = currentUserReactions => ({
  type: FETCH_CURRENT_USER_REACTIONS_SUCCESS,
  payload: { currentUserReactions },
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  return sdk.ownListings
    .query()
    .then(response => {
      const listings = response.data.data;
      const hasListings = listings && listings.length > 0;
      const lastListing = hasListings ? listings[0] : null;

      const hasPublishedListings =
        hasListings && !!listings.find(l => l.attributes.state === LISTING_STATE_PUBLISHED);

      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings, lastListing));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    per_page: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// About 20 page loaders dispatch fetchCurrentUser, so every navigation used to
// repeat the queries behind it, billed per request from 1 Dec 2026. The user
// record itself is still re-read every time (pages rely on it being fresh after
// a change); the extras behind it (has listings, has orders) are re-read at most
// every 5 minutes per user, or on demand with { refreshExtras: true }.
const EXTRAS_TTL_MS = 5 * 60 * 1000;

const extrasAreFresh = (state, userId) => {
  const { currentUserExtrasFetchedAt, currentUserExtrasUserId } = state.user;
  return (
    currentUserExtrasUserId === userId &&
    !!currentUserExtrasFetchedAt &&
    Date.now() - currentUserExtrasFetchedAt < EXTRAS_TTL_MS
  );
};

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

/**
 * Switch the site language (C40): remember the choice in a cookie (so a plain URL opens in
 * that language), save it on the signed-in user for emails (publicData.locale), then open the
 * same page under the other language's URL.
 */
export const changeLanguage = locale => (dispatch, getState, sdk) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;

  const { currentUser } = getState().user;
  const saveOnUser = currentUser
    ? sdk.currentUser
        .updateProfile({ publicData: { locale } })
        .catch(e => log.error(e, 'save-locale-failed', { locale }))
    : Promise.resolve();

  return saveOnUser.then(() => {
    const { pathname, search, hash } = window.location;
    goToUrl(pathForLocale(`${pathname}${search}${hash}`, locale));
  });
};

export const fetchCurrentUser = (params = null, { refreshExtras = false } = {}) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().Auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = params || {
    include: ['profileImage', 'stripeAccount'],
    ...imageVariantsForSets('avatar'),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      const currentUserReactions = getCurrentUserReactions(currentUser);

      if (currentUserReactions?.length > 0) {
        dispatch(fetchCurrentUserReactionsSuccess(currentUserReactions));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      const userId = currentUser.id.uuid;
      if (refreshExtras || !extrasAreFresh(getState(), userId)) {
        dispatch(currentUserExtrasFetched(userId, Date.now()));
        dispatch(fetchCurrentUserHasListings());

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Unread badges from our server; Sendbird itself connects only in
      // Conversations (see fetchUnreadCounts).
      dispatch(fetchUnreadCounts());

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

// Follow is a count, not a list: who I follow stays in my own privateData,
// and the followed person gets publicData.followerCount, kept by the server
// (server/api/users/follow.js).

const setFollowing = (followingId, follow) => (dispatch, getState, sdk) => {
  dispatch(follow ? followUserRequest() : unfollowUserRequest());
  return usersAPI
    .follow(followingId, follow)
    .then(response => {
      dispatch(follow ? followUserSuccess() : unfollowUserSuccess());
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      return dispatch(follow ? followUserError(error) : unfollowUserError(error));
    });
};

export const followUser = followingId => setFollowing(followingId, true);

export const unfollowUser = followingId => setFollowing(followingId, false);

export const addListingToWatchlist = listingId => (dispatch, getState, sdk) => {
  dispatch(addListingToWatchlistRequest(listingId));

  // Get currentUser from the redux store
  const { currentUser } = getState().user;

  // Array of currentUser watchlistIds
  const watchlistIdsFromPublicData = currentUser.attributes.profile.publicData.watchlistIds;

  // Form new array of watchlistIds
  const watchlistIds = watchlistIdsFromPublicData
    ? [listingId, ...watchlistIdsFromPublicData]
    : [listingId];

  return sdk.currentUser
    .updateProfile({
      publicData: {
        watchlistIds,
      },
    })
    .then(response => {
      dispatch(addListingToWatchlistSuccess());
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      notification.error(
        'Error adding listing to watchlist. Please refresh the page and try again.'
      );

      dispatch(addListingToWatchlistError(storableError(e), listingId));
    });
};

export const removeListingFromWatchlist = listingId => (dispatch, getState, sdk) => {
  dispatch(removeListingFromWatchlistRequest(listingId));

  // Get currentUser from the redux store
  const { currentUser } = getState().user;

  // Array of currentUser watchlistIds
  const watchlistIdsFromPublicData = currentUser.attributes.profile.publicData.watchlistIds || [];

  // Form new array of watchlistIds
  const watchlistIds = watchlistIdsFromPublicData.filter(id => id !== listingId);

  return sdk.currentUser
    .updateProfile({
      publicData: {
        watchlistIds,
      },
    })
    .then(response => {
      dispatch(removeListingFromWatchlistSuccess());
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      notification.error(
        'Error removing listing from watchlist. Please refresh the page and try again.'
      );
      dispatch(removeListingFromWatchlistError(storableError(e), listingId));
    });
};

// publicData.lastSeen is a "last active" date. Nothing displays it at the
// moment (ensureUserOnline is unused), and every write is billed, so it is
// written at most once a day: checked against the stored value, not only this
// tab's memory, so full page loads do not rewrite it.
const LAST_SEEN_THROTTLE_MS = 24 * 60 * 60 * 1000;
let lastSeenUpdatedAt = 0;

export const currentUserOnline = () => (dispatch, getState, sdk) => {
  const now = Date.now();
  const stored =
    Number(getState().user?.currentUser?.attributes?.profile?.publicData?.lastSeen) || 0;
  if (now - Math.max(lastSeenUpdatedAt, stored) < LAST_SEEN_THROTTLE_MS) {
    return Promise.resolve();
  }
  lastSeenUpdatedAt = now;

  dispatch(currentUserOnlineRequest());

  return sdk.currentUser
    .updateProfile({
      publicData: {
        lastSeen: now,
      },
    })
    .then(response => {
      dispatch(currentUserOnlineSuccess(response));
    })
    .catch(e => {
      lastSeenUpdatedAt = 0;
      dispatch(currentUserOnlineError(storableError(e)));
    });
};

export const saveRecentlyViewedListingId = listingId => (dispatch, getState, sdk) => {
  dispatch(saveRecentlyViewedListingRequest());

  const { currentUser } = getState().user;

  if (!currentUser) {
    // Save to local storage for guest users
    const recentlyViewedListingIdsFromLocalStorage =
      (isWindowDefined() && JSON.parse(localStorage.getItem('recentlyViewedListingIds'))) || [];
    if (!recentlyViewedListingIdsFromLocalStorage.includes(listingId)) {
      const updatedRecentlyViewedListingIds = [
        ...recentlyViewedListingIdsFromLocalStorage.slice(0, 9),
        listingId,
      ];
      if (isWindowDefined()) {
        localStorage.setItem(
          'recentlyViewedListingIds',
          JSON.stringify(updatedRecentlyViewedListingIds)
        );
      }
    }
    dispatch(saveRecentlyViewedListingSuccess());
    return Promise.resolve(recentlyViewedListingIdsFromLocalStorage);
  }

  const recentlyViewedListingIdsFromProtectedData =
    currentUser?.attributes?.profile?.protectedData?.recentlyViewedListingIds || [];

  if (recentlyViewedListingIdsFromProtectedData.includes(listingId)) {
    dispatch(saveRecentlyViewedListingSuccess());
    return Promise.resolve(currentUser);
  }

  const updatedRecentlyViewedListingIds = recentlyViewedListingIdsFromProtectedData.slice(0, 9);
  const recentlyViewedListingIds = [...updatedRecentlyViewedListingIds, listingId];

  return sdk.currentUser
    .updateProfile(
      {
        protectedData: {
          recentlyViewedListingIds,
        },
      },
      { expand: true }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);

      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveRecentlyViewedListingSuccess());
      return currentUser;
    })
    .catch(e => dispatch(saveRecentlyViewedListingError(storableError(e))));
};

export const queryRecentlyViewedListings = () => async (dispatch, getState, sdk) => {
  dispatch(queryRecentlyViewedListingsRequest());

  const { currentUser } = getState().user;

  let recentlyViewedListingIds = [];

  if (!currentUser) {
    // Retrieve recently viewed listing IDs from local storage for guest users
    recentlyViewedListingIds =
      (isWindowDefined() && JSON.parse(localStorage.getItem('recentlyViewedListingIds'))) || [];
  } else {
    // Retrieve recently viewed listing IDs from currentUser if available
    recentlyViewedListingIds =
      currentUser?.attributes?.profile?.protectedData?.recentlyViewedListingIds || [];
  }

  // Nothing viewed yet (always the case during server rendering, where there is
  // no localStorage): an empty ids filter is still a billed listings query, so
  // answer locally.
  if (!Array.isArray(recentlyViewedListingIds) || recentlyViewedListingIds.length === 0) {
    const empty = { data: { data: [], included: [] } };
    dispatch(queryRecentlyViewedListingsSuccess(empty));
    return empty;
  }

  return sdk.listings
    .query({
      ids: recentlyViewedListingIds,
      ...listingCardIncludeParams(),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(queryRecentlyViewedListingsSuccess(response));

      return response;
    })
    .catch(e => dispatch(queryRecentlyViewedListingsError(storableError(e))));
};

export const saveBillingDetails = billingDetails => (dispatch, getState, sdk) => {
  dispatch(saveBillingDetailsRequest());

  return sdk.currentUser
    .updateProfile({
      protectedData: {
        billingDetails,
      },
    })
    .then(response => {
      dispatch(saveBillingDetailsSuccess());
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => dispatch(saveBillingDetailsError(storableError(e))));
};

export const removeBillingDetails = () => (dispatch, getState, sdk) => {
  dispatch(removeBillingDetailsRequest());

  return sdk.currentUser
    .updateProfile({
      protectedData: {
        billingDetails: null,
      },
    })
    .then(response => {
      dispatch(removeBillingDetailsSuccess());
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => dispatch(removeBillingDetailsError(storableError(e))));
};

export const generateIntercomUserHash = userId => (dispatch, getState, sdk) => {
  dispatch(generateIntercomUserHashRequest());

  return intercomAPI.verification
    .generateHash({ userId })
    .then(response => {
      dispatch(generateIntercomUserHashSuccess(response.data.hash));
      return response.data.hash;
    })
    .catch(e => dispatch(generateIntercomUserHashError(storableError(e))));
};

/**
 * Upgrade an Audience user to Creator or Organization role.
 * This bypasses the signup flow for existing users.
 *
 * @param {string} newRole - The role to upgrade to ('creator' or 'organization')
 * @param {string} organizationName - Optional organization name (for organization role)
 */
/**
 * Upgrade an Audience user to Creator or Organization role.
 * This bypasses the signup flow for existing users.
 *
 * @param {string} newRole - The role to upgrade to ('creator' or 'organization')
 * @param {string} organizationName - Optional organization name (for organization role)
 */
export const upgradeUserRole = (newRole, organizationName = null) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(upgradeUserRoleRequest());

  const state = getState();
  const currentUser = state.user.currentUser;
  const firstName = currentUser?.attributes?.profile?.publicData?.name?.firstName || 'User';
  const userId = currentUser?.id?.uuid;

  // Build the profile update params for publicData
  const updateParams = {
    publicData: {
      role: newRole,
    },
  };

  let organizationMetadata = null;

  // If upgrading to organization, we need to create organization profile
  if (newRole === 'organization') {
    const {
      createDefaultOrganizationProfile,
      ownedOrganizationIds,
    } = require('../util/organizations');
    const organizationData = createDefaultOrganizationProfile(organizationName, firstName);

    // Merge organization profiles with existing ones (if any)
    const existingProfiles =
      currentUser?.attributes?.profile?.publicData?.organizationProfiles || [];
    updateParams.publicData.organizationProfiles = [
      ...existingProfiles,
      ...organizationData.publicDataProfiles,
    ];
    updateParams.publicData.organizationProfileIds = ownedOrganizationIds(
      updateParams.publicData.organizationProfiles
    );

    // Store metadata to save via server endpoint
    organizationMetadata = organizationData.metadata;
  }

  try {
    // First, update publicData via SDK
    const response = await sdk.currentUser.updateProfile(updateParams);
    dispatch(addMarketplaceEntities(response));

    // If organization, save metadata via server endpoint
    if (organizationMetadata && userId) {
      try {
        await usersAPI.updateProfile({
          id: userId,
          metadata: organizationMetadata,
        });
      } catch (metadataError) {
        console.error('Failed to save organization metadata during upgrade:', metadataError);
        // Don't fail the upgrade if metadata save fails, but log the error
      }
    }

    dispatch(upgradeUserRoleSuccess());

    // Refresh current user to get updated data
    await dispatch(fetchCurrentUser());

    // Return the org profile ID if upgrading to organization (needed for redirect)
    const updatedUser = getState().user.currentUser;
    const orgProfiles = updatedUser?.attributes?.profile?.publicData?.organizationProfiles || [];
    const orgId = orgProfiles.length > 0 ? orgProfiles[0].id : null;

    return { role: newRole, orgId };
  } catch (e) {
    dispatch(upgradeUserRoleError(storableError(e)));
    throw e;
  }
};
